import { createId } from "@paralleldrive/cuid2";
import { defaultResumeData, ResumeData, resumeDataSchema } from "@reactive-resume/schema";
import { colorList, layoutsList, marginList, Template } from "@reactive-resume/utils";
import { z } from "zod";

import { ResumeIntakeState } from "../_types/resumeIntake";

const emailSchema = z.string().email();
const urlSchema = z.string().url();

const cloneDefaultResumeData = () => JSON.parse(JSON.stringify(defaultResumeData)) as ResumeData;

const uniqueValues = (values: string[]) =>
  Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const isHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const toParagraphHtml = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (isHtml(trimmed)) return trimmed;

  return `<p>${escapeHtml(trimmed)}</p>`;
};

const splitTextLines = (value: string) =>
  value
    .split(/\r?\n|(?:^|\s)[*-]\s+/)
    .map((line) => line.trim())
    .filter(Boolean);

const toBulletHtml = (values: string[]) => {
  const bullets = uniqueValues(values.flatMap(splitTextLines));
  if (!bullets.length) return "";

  return `<ul>${bullets.map((bullet) => `<li><p>${escapeHtml(bullet)}</p></li>`).join("")}</ul>`;
};

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return urlSchema.parse(candidate);
  } catch {
    return "";
  }
};

const normalizeEmail = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    return emailSchema.parse(trimmed);
  } catch {
    return "";
  }
};

const createUrl = (href: string) => ({
  label: "",
  href: normalizeUrl(href),
});

const getLinkedInUsername = (href: string) => {
  try {
    const url = new URL(href);
    const pathParts = url.pathname.split("/").filter(Boolean);
    return pathParts[pathParts.length - 1] || "";
  } catch {
    return "";
  }
};

export const applyResumeTemplateMetadata = (resumeData: ResumeData, template: string) => {
  const defaultColor = "#475569";
  const defaultMargin = 14;

  resumeData.metadata.template = template;

  if (template in layoutsList) {
    const typedTemplate = template as Template;
    resumeData.metadata.layout = layoutsList[typedTemplate];
    resumeData.metadata.theme.primary = colorList[typedTemplate] || defaultColor;
    resumeData.metadata.page.margin = marginList[typedTemplate] || defaultMargin;
  }

  return resumeData;
};

export const buildResumeDataFromIntake = (intake: ResumeIntakeState) => {
  const resumeData = cloneDefaultResumeData();

  resumeData.basics.name = intake.basics.name.trim();
  resumeData.basics.headline = intake.basics.headline.trim();
  resumeData.basics.email = normalizeEmail(intake.basics.email);
  resumeData.basics.phone = intake.basics.phone.trim();
  resumeData.basics.location = intake.basics.location.trim();

  const linkedInHref = normalizeUrl(intake.basics.linkedin);
  if (linkedInHref) {
    resumeData.sections.profiles.items.push({
      id: createId(),
      visible: true,
      network: "LinkedIn",
      username: getLinkedInUsername(linkedInHref),
      icon: "linkedin",
      url: {
        label: "",
        href: linkedInHref,
      },
    });
  }

  resumeData.sections.summary.content = toParagraphHtml(intake.summary);

  resumeData.sections.experience.items = intake.experience
    .filter((experience) =>
      Boolean(
        experience.company.trim() ||
          experience.position.trim() ||
          experience.customSummary.trim() ||
          experience.rawResponsibilities.trim() ||
          experience.selectedBullets.length,
      ),
    )
    .map((experience) => ({
      id: createId(),
      visible: true,
      company: experience.company.trim() || "Experience",
      position: experience.position.trim(),
      location: experience.location.trim(),
      date: experience.date.trim(),
      summary:
        toBulletHtml(experience.selectedBullets) ||
        toBulletHtml([experience.customSummary]) ||
        toBulletHtml([experience.rawResponsibilities]),
      url: createUrl(""),
    }));

  resumeData.sections.skills.items = uniqueValues([
    ...intake.skills.selected,
    ...intake.skills.custom,
  ]).map((skill) => ({
    id: createId(),
    visible: true,
    name: skill,
    description: "",
    level: 0,
    keywords: [],
  }));

  resumeData.sections.education.items = intake.education
    .filter((education) =>
      Boolean(
        education.institution.trim() ||
          education.studyType.trim() ||
          education.area.trim() ||
          education.date.trim(),
      ),
    )
    .map((education) => ({
      id: createId(),
      visible: true,
      institution: education.institution.trim() || "Education",
      studyType: education.studyType.trim(),
      area: education.area.trim(),
      score: education.score.trim(),
      date: education.date.trim(),
      summary: toParagraphHtml(education.summary),
      url: createUrl(""),
    }));

  resumeData.sections.awards.items = uniqueValues(intake.extras.awards).map((award) => ({
    id: createId(),
    visible: true,
    title: award,
    awarder: "",
    date: "",
    summary: "",
    url: createUrl(""),
  }));

  resumeData.sections.certifications.items = uniqueValues(intake.extras.certifications).map(
    (certification) => ({
      id: createId(),
      visible: true,
      name: certification,
      issuer: "",
      date: "",
      summary: "",
      url: createUrl(""),
    }),
  );

  resumeData.sections.languages.items = uniqueValues(intake.extras.languages).map((language) => ({
    id: createId(),
    visible: true,
    name: language,
    description: "",
    level: 1,
  }));

  resumeData.sections.interests.items = uniqueValues(intake.extras.interests).map((interest) => ({
    id: createId(),
    visible: true,
    name: interest,
    keywords: [],
  }));

  applyResumeTemplateMetadata(resumeData, intake.template);

  return resumeDataSchema.parse(resumeData);
};

